import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router';
import { authGuard } from '@auth0/auth0-vue';
import {
  defaultAdviserAuthGuard,
  adviserProductAccessGuard,
  insuranceAdviserAuthGuardWithRedirect,
} from './route-guard';
import { userStore } from '@/stores/UserStore';

const showHomeView = process.env.VUE_APP_SHOW_HOME_VIEW === 'true';

const routes: Array<RouteRecordRaw> = [
  showHomeView
    ? {
        path: '/',
        name: 'home',
        beforeEnter: [authGuard, adviserProductAccessGuard],
        component: () => import('@/pages/HomeView.vue'),
      }
    : {
        path: '/',
        name: 'home',
        redirect: '/products/insurance',
      },
  {
    path: '/products/insurance',
    name: 'insurance-product',
    beforeEnter: [authGuard, insuranceAdviserAuthGuardWithRedirect],
    component: () => import('@/pages/products/insurance/InsuranceHome.vue'),
    children: [
      {
        path: '',
        name: 'insurance-home-default',
        component: () =>
          import(
            '@/pages/products/insurance/insurance-home-default/InsuranceHomeDefault.vue'
          ),
      },
      {
        path: 'quote-and-apply',
        name: 'insurance-quote-and-apply',
        component: () =>
          import(
            '@/pages/products/insurance/quote-and-apply/QuoteAndApply.vue'
          ),
      },
      {
        path: 'quote-and-apply/application',
        name: 'insurance-application',
        component: () =>
          import(
            '@/pages/products/insurance/application/ApplicantDetailsCheck.vue'
          ),
      },
      {
        path: 'applications/:id',
        name: 'applications-details',
        component: () =>
          import(
            '@/pages/products/insurance/policies-and-application/ApplicationDetails.vue'
          ),
      },
      {
        path: 'policies/:id',
        name: 'policies-details',
        component: () =>
          import(
            '@/pages/products/insurance/policies-and-application/PolicyDetails.vue'
          ),
      },
      {
        path: '/tools-forms-docs/UpcomingRenewals',
        alias: '/tools-forms-docs/upcoming-renewals-next-90-days',
        name: 'UpcomingRenewals',
        beforeEnter: [authGuard, adviserProductAccessGuard],
        component: () =>
          import(
            '@/pages/products/insurance/upcoming-renewals/UpcomingRenewals.vue'
          ),
      },
    ],
  },
  {
    path: '/products/investments',
    name: 'investment-product',
    beforeEnter: [authGuard, adviserProductAccessGuard],
    component: () => import('@/pages/products/investments/InvestmentHome.vue'),
    children: [
      {
        path: '',
        name: 'investment-home-default',
        component: () =>
          import(
            '@/pages/products/investments/investment-home-default/InvestmentHomeDefault.vue'
          ),
      },
      {
        path: 'account-details/:id',
        name: 'client-account-details',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-details/InvestmentAccountDetail.vue'
          ),
      },
      {
        path: 'update-account/:id',
        name: 'update-client-account',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/InvestmentAccountUpdate.vue'
          ),
      },
      {
        path: 'add-account/:id',
        name: 'add-new-account',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/components/AddNewContact.vue'
          ),
      },
      {
        path: 'update-contact/:id/:contactId',
        name: 'update-contact',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/components/AddNewContact.vue'
          ),
      },
      {
        path: 'add-user/:clientId',
        name: 'add-user',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/components/UserForm.vue'
          ),
      },
      {
        path: 'edit-user/:clientId/:userId',
        name: 'edit-user',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/components/UserForm.vue'
          ),
      },
      {
        path: 'invest-withdraw-transfer/:id',
        name: 'invest-withdraw',
        component: () =>
          import(
            '@/pages/products/investments/invest-withdraw-transfer/InvestWithdrawTransfer.vue'
          ),
      },
      {
        path: 'mybooster-user/:id',
        name: 'my-booster-user',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/components/MyBoosterUsers.vue'
          ),
      },
      {
        path: 'regular-details/:id',
        name: 'regular-details',
        component: () =>
          import(
            '@/pages/products/investments/investment-regulars-list/InvestmentsRegularDetails.vue'
          ),
      },
      {
        path: 'client-regular-details/:id',
        name: 'client-regular-details',
        component: () =>
          import(
            '@/pages/products/investments/investment-regulars-list/InvestmentsRegularDetails.vue'
          ),
      },
      {
        path: 'search-transactions/:clientId/:accountId',
        name: 'search-transactions',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-details/components/InvestmentSecuritiesSearch/InvestmentSecuritySearch.vue'
          ),
      },
      {
        path: 'add-bank-account/:id',
        name: 'add-bank-account',
        component: () =>
          import(
            '@/pages/products/investments/investment-account-update/components/AddBankAccount.vue'
          ),
      },
    ],
  },
  {
    path: '/accounts-and-clients',
    alias: '/clients',
    name: 'clients',
    beforeEnter: [authGuard, adviserProductAccessGuard],
    component: () => import('@/pages/AdviserHubClients.vue'),
  }, 
  {
    path: '/tools-forms-docs',
    name: 'tools-forms-docs',
    beforeEnter: [authGuard, defaultAdviserAuthGuard],
    component: () => import('@/pages/tools-forms-docs/ToolsFormsDocs.vue'),
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('@/pages/ErrorView.vue'),
  },
  {
    path: '/:catchAll(.*)',
    redirect: { name: 'error' },
  },
];

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
});

// Work around to fix the auth0 lib redirection loosing base url issue
router.beforeEach(() => {
  const user = userStore();
  user.setLoading(true);
  if (
    !window.location.href.includes(process.env.BASE_URL) &&
    process.env.BASE_URL.length > 1
  ) {
    window.location.href = window.location.origin + process.env.BASE_URL + '#/';
  }
});

// Set loading to false after every navigation (including redirects)
router.afterEach(() => {
  const user = userStore();
  user.setLoading?.(false); // If using store
  // Or, if using an event or global variable, trigger it here
});

export default router;
